import type { MetaEvent } from "@fab-judge/schema";
import { API_BASE, authedFetch, readApiError } from "./client";

export interface ConversationSummary {
  id: string;
  title: string | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  meta: Omit<MetaEvent, "type"> | null;
  createdAt: string;
}

export interface ConversationDetail {
  id: string;
  title: string | null;
  createdAt: string;
  messages: ConversationMessage[];
}

export interface ShareState {
  id: string;
  isPublic: true;
  url: string;
}

export interface ConversationPage {
  conversations: ConversationSummary[];
  hasMore: boolean;
}

export async function listConversations(
  options: { limit?: number; offset?: number } = {},
): Promise<ConversationPage> {
  const params = new URLSearchParams();
  if (options.limit != null) params.set("limit", String(options.limit));
  if (options.offset != null) params.set("offset", String(options.offset));
  const query = params.toString();
  const response = await authedFetch(
    `/conversation${query ? `?${query}` : ""}`,
  );
  if (!response.ok) throw await readApiError(response);
  return response.json();
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const response = await authedFetch(`/conversation/${encodeURIComponent(id)}`);
  if (!response.ok) throw await readApiError(response);
  return response.json();
}

/**
 * Delete a conversation from the owner's history. The server removes the
 * conversation and its messages; cached answers are kept (unlinked from the
 * account). Idempotent from the client's point of view (404 if already gone).
 */
export async function deleteConversation(id: string): Promise<void> {
  const response = await authedFetch(
    `/conversation/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
  if (!response.ok) throw await readApiError(response);
}

/**
 * Make a conversation public. Sharing is one-way and idempotent: a conversation
 * cannot be un-shared once public.
 */
export async function shareConversation(id: string): Promise<ShareState> {
  const response = await authedFetch(
    `/conversation/${encodeURIComponent(id)}/share`,
    { method: "POST" },
  );
  if (!response.ok) throw await readApiError(response);
  return response.json();
}

/** Public read of a shared conversation (no auth). */
export async function getSharedConversation(
  id: string,
): Promise<ConversationDetail> {
  const response = await fetch(`${API_BASE}/shared/${encodeURIComponent(id)}`);
  if (!response.ok) throw await readApiError(response);
  return response.json();
}
